import env from '../config/env';
import * as realApi from '../api/real/productsApi';
import {
  products as mockProductList,
  getProductByHandle as mockGetByHandle,
  getBestsellers as mockGetBestsellers,
  getNewArrivals as mockGetNewArrivals,
  getProductsByType as mockGetByType,
  searchProducts as mockSearch,
  getProductsByCollection as mockGetByCollection,
} from '../data/mockProducts';

// Normalize backend DTO fields to the shape frontend components expect.
const normalizeProduct = (p) => {
  const imageUrls = (p.images || [])
    .map((img) => (typeof img === 'string' ? img : img.url || img.src || ''))
    .filter(Boolean);
  if (!imageUrls.length && p.primaryImageUrl) imageUrls.push(p.primaryImageUrl);

  const firstVariant = p.variants?.[0];
  const priceVal = p.price ?? p.minPrice ?? (firstVariant?.price != null ? Number(firstVariant.price) : 0);
  const compareVal = p.compareAtPrice ?? p.maxPrice ?? (firstVariant?.compareAtPrice != null ? Number(firstVariant.compareAtPrice) : null);

  const variantInventory = p.variants?.length
    ? p.variants.reduce((s, v) => s + (v.inventoryQuantity || 0), 0)
    : undefined;
  const inventoryQty = p.inventory ?? p.totalInventory ?? variantInventory ?? 0;

  return {
    ...p,
    type: p.type || p.productType || '',
    vendor: p.vendor || '',
    price: priceVal,
    compareAtPrice: compareVal,
    // Market-specific price (present when browsing a non-default market)
    marketPrice: p.marketPrice != null ? Number(p.marketPrice) : null,
    marketCurrencyCode: p.marketCurrencyCode ?? null,
    marketCurrencySymbol: p.marketCurrencySymbol ?? null,
    images: imageUrls,
    inventory: inventoryQty,
    rating: p.rating ?? 0,
    reviewCount: p.reviewCount ?? 0,
    skinType: p.skinType || p.skinProfile?.skinTypes || [],
    isBestseller: p.isBestseller ?? false,
    isNew: p.isNew ?? false,
  };
};

const logApiError = (fn, err) => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      `%c[productService] ${fn}: ${err?.message ?? err}`,
      'color:#F59E0B;font-weight:bold'
    );
  }
};

export const getProducts = async ({ page = 0, size = 20, sort, type, vendor, marketCode } = {}) => {
  if (env.isMock) {
    let list = [...mockProductList];
    if (type) list = list.filter((p) => p.type === type);
    if (vendor) list = list.filter((p) => p.vendor === vendor);
    const start = page * size;
    return list.slice(start, start + size);
  }
  try {
    const list = await realApi.getProducts({ page, size, sort, marketCode });
    return list.map(normalizeProduct);
  } catch (err) {
    logApiError('getProducts', err);
    return [];
  }
};

const CATALOG_LIMIT = 2000; // whole catalogue; 200 hid every product after the 200th (the store has had 444)
const CATALOG_CHUNK = 100; // 2 requests of 100 — first one paints fast, second loads in parallel with rendering

/**
 * Loads the full storefront catalog in two chunks instead of one 200-row request.
 * `onFirstChunk(products)` fires as soon as chunk 1 arrives (render early); the returned
 * promise resolves with every product once the remaining chunks are merged in.
 */
export const getAllProducts = async ({ marketCode, onFirstChunk } = {}) => {
  if (env.isMock) {
    const all = await getProducts({ size: CATALOG_LIMIT, marketCode });
    if (onFirstChunk) onFirstChunk(all);
    return all;
  }
  try {
    const first = await realApi.getProductsPage({ page: 0, size: CATALOG_CHUNK, marketCode });
    const firstItems = first.items.map(normalizeProduct);
    if (onFirstChunk) onFirstChunk(firstItems);
    const lastPage = Math.min(first.totalPages, Math.ceil(CATALOG_LIMIT / CATALOG_CHUNK));
    if (lastPage <= 1) return firstItems;
    const rest = await Promise.all(
      Array.from({ length: lastPage - 1 }, (_, i) =>
        realApi.getProductsPage({ page: i + 1, size: CATALOG_CHUNK, marketCode })),
    );
    return [...firstItems, ...rest.flatMap((r) => r.items.map(normalizeProduct))];
  } catch (err) {
    logApiError('getAllProducts', err);
    return [];
  }
};

export const getProductByHandle = async (handle, marketCode) => {
  if (env.isMock) return mockGetByHandle(handle) || null;
  try {
    const p = await realApi.getProductByHandle(handle, marketCode);
    return p ? normalizeProduct(p) : null;
  } catch (err) {
    logApiError('getProductByHandle', err);
    return null;
  }
};

export const searchProducts = async (query, marketCode) => {
  if (env.isMock) return mockSearch(query);
  try {
    const list = await realApi.searchProducts(query, marketCode);
    return list.map(normalizeProduct);
  } catch (err) {
    logApiError('searchProducts', err);
    return [];
  }
};

export const getBestsellers = async (size = 8, marketCode) => {
  if (env.isMock) return mockGetBestsellers().slice(0, size);
  try {
    const list = await realApi.getBestsellers(size, marketCode);
    return list.map(normalizeProduct);
  } catch (err) {
    logApiError('getBestsellers', err);
    return [];
  }
};

export const getNewArrivals = async (size = 8, marketCode) => {
  if (env.isMock) return mockGetNewArrivals().slice(0, size);
  try {
    const list = await realApi.getNewArrivals(size, marketCode);
    return list.map(normalizeProduct);
  } catch (err) {
    logApiError('getNewArrivals', err);
    return [];
  }
};

export const getProductsByType = async (type, size = 20, marketCode) => {
  if (env.isMock) return mockGetByType(type).slice(0, size);
  try {
    const list = await realApi.getProductsByType(type, size, marketCode);
    return list.map(normalizeProduct);
  } catch (err) {
    logApiError('getProductsByType', err);
    return [];
  }
};

export const getProductsByCollection = async (handle, size = 20, marketCode) => {
  if (env.isMock) return mockGetByCollection(handle).slice(0, size);
  try {
    const list = await realApi.getProductsByCollection(handle, size, marketCode);
    return list.map(normalizeProduct);
  } catch (err) {
    logApiError('getProductsByCollection', err);
    return [];
  }
};

export const getCollections = async () => {
  if (env.isMock) return [];
  try {
    return await realApi.getCollections();
  } catch (err) {
    logApiError('getCollections', err);
    return [];
  }
};
